import { captureException } from "@sentry/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";

import { useCharacterSpeech } from "@/hooks/useCharacterSpeech";
import { aTalkerClient } from "@/libs/apiClient";
import {
  type ChatMessage,
  type MiiboRequest,
  type MiiboResponse,
} from "@/types/miibo.schema";

const uid = uuid();

type ChatOptions = {
  label: string;
  value: string;
};

type Props = {
  code: string;
};

export type MiiboHookTypes = {
  talkMiibo: (text: string) => void;
  options: string[];
  userState: {
    [key: string]: string;
  };
  chatLog: ChatMessage[];
  isLoading: boolean;
  lastAssistantMessage: string;
  chatOptions: ChatOptions[];
  sendChat: (text: string) => void;
  startMiibo: (newNeedTTS?: boolean) => void;
  reset: () => void;
  isSpeaking: boolean;
};

export const postMiibo = async (
  requestBody: MiiboRequest,
): Promise<MiiboResponse> => {
  const res = await aTalkerClient.post<MiiboResponse>("miibo/", requestBody);
  return res.data;
};

export const useMiibo = ({ code }: Props): MiiboHookTypes => {
  const [needTTS, setNeedTTS] = useState<boolean>(true);
  const [chatLog, setChatLog] = useState<ChatMessage[]>([]);

  const [userState, setUserState] = useState<{
    [key: string]: string;
  }>({});

  const [options, setOptions] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { playAudio, cancelSpeech, isCharacterSpeaking } = useCharacterSpeech();

  const requestMiibo = useCallback(
    async (text: string): Promise<MiiboResponse | undefined> => {
      if (code === "") return undefined;
      try {
        const response = await postMiibo({
          code,
          uid,
          utterance: text,
        });
        return response;
      } catch (error) {
        captureException(error);
        return undefined;
      }
    },
    [code],
  );

  const talkMiibo = useCallback(
    async (text: string) => {
      setIsLoading(true);
      if (text.length > 0) {
        setChatLog((prev) => [
          ...prev,
          {
            content: text,
            role: "user",
          },
        ]);
      }
      const res = await requestMiibo(text);
      if (res) {
        const urlPattern = new RegExp(`${res.bestResponse.url}$`);
        const filteredUtterance = res.bestResponse.utterance
          .replace(urlPattern, "")
          .trim();
        setUserState(res.userState);
        setOptions(res.bestResponse.options);
        setChatLog((prev) => [
          ...prev,
          {
            content: filteredUtterance,
            role: "assistant",
          },
        ]);
      }
      setIsLoading(false);
    },
    [requestMiibo, setChatLog],
  );

  const startMiibo = useCallback(
    (newNeedTTS?: boolean) => {
      if (newNeedTTS !== undefined) setNeedTTS(newNeedTTS);
      if (code) talkMiibo("");
    },
    [code, talkMiibo],
  );

  const lastAssistantMessage = useMemo((): string => {
    const lastMessage =
      chatLog
        .slice()
        .reverse()
        .find((log) => log.role === "assistant")?.content || "";
    return lastMessage;
  }, [chatLog]);

  const sendChat = useCallback(
    (message: string): void => {
      if (message.length > 0) {
        cancelSpeech();
        setOptions([]);
        talkMiibo(message);
      }
    },
    [cancelSpeech, talkMiibo],
  );

  const chatOptions = useMemo(
    (): { label: string; value: string }[] =>
      options.map((option) => ({
        label: option,
        value: option,
      })),
    [options],
  );

  useEffect(() => {
    if (lastAssistantMessage !== "" && needTTS) {
      (async () => {
        await playAudio(lastAssistantMessage);
      })();
    }
  }, [lastAssistantMessage, needTTS]);

  const reset = useCallback(() => {
    cancelSpeech();
    setChatLog([]);
    setUserState({});
    setOptions([]);
    setTimeout(() => {
      startMiibo();
    }, 300);
  }, [cancelSpeech, startMiibo]);

  return {
    talkMiibo,
    userState,
    options,
    chatLog,
    isLoading,
    lastAssistantMessage,
    sendChat,
    chatOptions,
    startMiibo,
    reset,
    isSpeaking: isCharacterSpeaking,
  };
};
